import { useEffect, useState } from 'react';

import type {
  RoleMappingRequest,
  RoleMappingResponse,
} from '@revelio/data-access';

import { normalizeForMatch } from '../utils/normalizeForMatch';
import {
  parseId,
  type RoleGranularity,
  type RoleLevel,
} from './useResolveProfileIds';

// ── Types ──

export interface ResolvedRole {
  roleId: number | null;
  roleName: string | null;
  roleLevels: RoleLevel[];
}

export const EMPTY_ROLE: ResolvedRole = {
  roleId: null,
  roleName: null,
  roleLevels: [],
};

/** Generic shape of a role item from the selection list query. */
export interface RoleListItem {
  id?: string | number | null;
  shortName?: string | null;
  longName?: string | null;
  role_k7?: string | number | null;
  role_k50?: string | number | null;
  role_k150?: string | number | null;
  role_k500?: string | number | null;
  role_k1500?: string | number | null;
  role_k5000?: string | number | null;
  rawTitles?: string[];
}

/** All role level lists (sourced from the role TaxonomyTree), keyed by level name. */
export interface RoleSelectionData {
  role_k7: RoleListItem[];
  role_k50: RoleListItem[];
  role_k150: RoleListItem[];
  role_k500: RoleListItem[];
  role_k1500: RoleListItem[];
  role_k5000: RoleListItem[];
  role_k15000: RoleListItem[];
}

// ── Helpers ──

const ROLE_MAPPER_URL = '/api/ai-search/mapper/role';

const mapApiLevelToSelectionListLevel = (apiLevel: string): string => {
  if (apiLevel === 'role_k10_short') return 'role_k7';
  if (apiLevel === 'role_k10000') return 'role_k15000';
  return apiLevel;
};

const fetchRoleMapping = async (
  title: string,
  signal?: AbortSignal
): Promise<RoleMappingResponse> => {
  const body: RoleMappingRequest = {
    titles: title,
    taxonomy_id: 0,
  };

  const response = await fetch(ROLE_MAPPER_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    throw new Error(`Role mapper request failed (${response.status})`);
  }

  return (await response.json()) as RoleMappingResponse;
};

/** Find a role by exact name match (longName or shortName) in a list. */
const findRoleByName = (
  roleList: RoleListItem[],
  roleName: string
): RoleListItem | undefined => {
  const normalized = normalizeForMatch(roleName);
  return roleList.find(
    (item) =>
      normalizeForMatch(item.longName) === normalized ||
      normalizeForMatch(item.shortName) === normalized
  );
};

/** Helper to look up a parent item by ID in a given level list. */
const lookupItemById = (
  list: RoleListItem[],
  id: number
): RoleListItem | undefined => list.find((item) => parseId(item.id) === id);

const itemName = (item: RoleListItem | undefined, fallback: string): string =>
  (item?.longName || item?.shortName || fallback) as string;

/**
 * Build the full role levels hierarchy from a matched item at any level.
 * Uses parent ID fields to traverse upward; returns levels from finest to broadest.
 * Only includes levels supported by PayV2 (k15000, k1500, k150, k7).
 */
const buildRoleLevels = (
  matchedItem: RoleListItem,
  sourceLevel: string,
  data: RoleSelectionData
): RoleLevel[] => {
  const levels: RoleLevel[] = [];

  // PayV2-supported levels from finest to broadest
  const fineToCoarse: Array<{
    type: RoleGranularity;
    parentKey: keyof RoleListItem | null;
    list: RoleListItem[];
  }> = [
    { type: 'role_k15000', parentKey: 'role_k1500', list: data.role_k15000 },
    { type: 'role_k1500', parentKey: 'role_k150', list: data.role_k1500 },
    { type: 'role_k150', parentKey: 'role_k7', list: data.role_k150 },
    { type: 'role_k7', parentKey: null, list: data.role_k7 },
  ];

  const sourceIdx = fineToCoarse.findIndex((l) => l.type === sourceLevel);

  let currentItem: RoleListItem | undefined = matchedItem;

  for (let i = 0; i < fineToCoarse.length; i++) {
    const level = fineToCoarse[i];
    if (!level) continue;

    if (level.type === sourceLevel) {
      const id = parseId(matchedItem.id);
      if (id !== null) {
        levels.push({
          id,
          type: level.type,
          levelId: level.type,
          name: itemName(matchedItem, 'Role'),
        });
      }
      currentItem = matchedItem;
    } else if (i > sourceIdx && sourceIdx >= 0) {
      const parentKey = fineToCoarse[i - 1]?.parentKey;
      if (!parentKey || !currentItem) continue;
      const parentId = parseId(
        currentItem[parentKey] as string | number | null | undefined
      );
      if (parentId === null) continue;
      const parentItem = lookupItemById(level.list, parentId);
      if (parentItem) {
        levels.push({
          id: parentId,
          type: level.type,
          levelId: level.type,
          name: itemName(parentItem, `${level.type} #${parentId}`),
        });
        currentItem = parentItem;
      }
    } else if (sourceIdx < 0) {
      const parentId = parseId(
        matchedItem[level.type as keyof RoleListItem] as
          | string
          | number
          | null
          | undefined
      );
      if (parentId !== null) {
        const item = lookupItemById(level.list, parentId);
        if (item) {
          levels.push({
            id: parentId,
            type: level.type,
            levelId: level.type,
            name: itemName(item, `${level.type} #${parentId}`),
          });
          currentItem = item;
        }
      }
    }
  }

  return levels;
};

/**
 * Build a ResolvedRole from a matched item at any level.
 * The roleId / roleName reflect the k150 ancestor (for backward compat),
 * while roleLevels includes all PayV2-supported granularities.
 */
const buildResolvedRole = (
  matchedItem: RoleListItem,
  sourceLevel: string,
  data: RoleSelectionData
): ResolvedRole | null => {
  const roleLevels = buildRoleLevels(matchedItem, sourceLevel, data);
  if (roleLevels.length === 0) return null;

  const k150Level = roleLevels.find((l) => l.type === 'role_k150');
  const primary = k150Level ?? roleLevels[0];
  if (!primary) return null;

  return {
    roleId: primary.id,
    roleName: primary.name,
    roleLevels,
  };
};

/** Levels that are supported in the selection list data. */
const SELECTION_LIST_LEVELS = [
  'role_k7',
  'role_k50',
  'role_k150',
  'role_k500',
  'role_k1500',
  'role_k5000',
  'role_k15000',
] as const;

type SelectionListLevel = (typeof SELECTION_LIST_LEVELS)[number];

/**
 * Process a single API result:
 * 1. Map selected_level → selection list level name
 * 2. Find mapped_role by exact name in that level's list
 * 3. Build role levels (k150 + k7)
 */
const resolveApiResult = (
  result: { mapped_role: string; selected_level: string },
  data: RoleSelectionData
): ResolvedRole | null => {
  const selectionListLevel = mapApiLevelToSelectionListLevel(
    result.selected_level
  ) as SelectionListLevel;

  const levelList = data[selectionListLevel];
  if (!levelList || levelList.length === 0) {
    return null;
  }

  const match = findRoleByName(levelList, result.mapped_role);
  if (!match) {
    return null;
  }

  const resolved = buildResolvedRole(match, selectionListLevel, data);
  return resolved;
};

/**
 * Fallback role resolution using rawTitles and substring matching.
 */
const fallbackToLocalMatching = (
  title: string,
  data: RoleSelectionData,
  setRole: (result: ResolvedRole) => void
): void => {
  const normalizedTitle = normalizeForMatch(title);
  const k150List = data.role_k150;

  const MIN_SUBSTRING_LEN = 8;

  const rawTitleMatch = k150List.find((item) =>
    (item.rawTitles ?? []).some((raw) => {
      const normalizedRaw = normalizeForMatch(raw);
      if (!normalizedRaw) return false;
      if (normalizedRaw === normalizedTitle) return true;
      if (
        normalizedRaw.length >= MIN_SUBSTRING_LEN &&
        normalizedTitle.includes(normalizedRaw)
      ) {
        return true;
      }
      if (
        normalizedTitle.length >= MIN_SUBSTRING_LEN &&
        normalizedRaw.includes(normalizedTitle)
      ) {
        return true;
      }
      return false;
    })
  );

  if (rawTitleMatch) {
    const resolved = buildResolvedRole(rawTitleMatch, 'role_k150', data);
    if (resolved) {
      setRole(resolved);
      return;
    }
  }

  const nameMatch = k150List.find((item) => {
    const short = normalizeForMatch(item.shortName);
    const long = normalizeForMatch(item.longName);
    if (short === normalizedTitle || long === normalizedTitle) return true;
    if (
      short &&
      short.length >= MIN_SUBSTRING_LEN &&
      normalizedTitle.includes(short)
    ) {
      return true;
    }
    if (
      long &&
      long.length >= MIN_SUBSTRING_LEN &&
      normalizedTitle.includes(long)
    ) {
      return true;
    }
    return false;
  });

  if (nameMatch) {
    const resolved = buildResolvedRole(nameMatch, 'role_k150', data);
    if (resolved) {
      setRole(resolved);
      return;
    }
  }

  setRole(EMPTY_ROLE);
};

// ── Hook ──

/**
 * Custom hook that calls the role mapping API and resolves the result
 * to a role_k150 selection list ID plus parent level IDs.
 */
export const useRoleMapping = (
  title: string | null | undefined,
  roleSelectionData: RoleSelectionData | null
): { role: ResolvedRole; isLoading: boolean } => {
  const [role, setRole] = useState<ResolvedRole>(EMPTY_ROLE);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!title) {
      setRole(EMPTY_ROLE);
      return;
    }

    if (!roleSelectionData) return;

    const controller = new AbortController();
    setIsLoading(true);

    const resolve = async () => {
      try {
        const apiResults = await fetchRoleMapping(title, controller.signal);

        if (apiResults.length > 0) {
          const firstResult = apiResults[0];
          if (firstResult) {
            const resolved = resolveApiResult(firstResult, roleSelectionData);
            if (resolved) {
              setRole(resolved);
              return;
            }

            if (firstResult.top_5_matches) {
              for (const candidate of firstResult.top_5_matches) {
                const candidateResolved = resolveApiResult(
                  {
                    mapped_role: candidate.role,
                    selected_level: candidate.level,
                  },
                  roleSelectionData
                );
                if (candidateResolved) {
                  setRole(candidateResolved);
                  return;
                }
              }
            }
          }
        }

        fallbackToLocalMatching(title, roleSelectionData, setRole);
      } catch (err: unknown) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        console.warn('Role mapping API failed, using fallback:', err);
        fallbackToLocalMatching(title, roleSelectionData, setRole);
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    resolve();

    return () => controller.abort();
  }, [title, roleSelectionData]);

  return { role, isLoading };
};
